import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';

@Component
export class DeliveryCountdown extends ThemeComponent {
    @Prop({default: null}) selectedVariant!: Shopify.Liquid.Variant|null;
    @Prop display!: boolean;
    @Prop nextDayMessage!: string;
    @Prop countdownMessage!: string;
    @Prop daylightSaving!: boolean;
    @Prop maxHour!: number;

    @Ref() countdown!: HTMLDivElement;
    @Ref({required: false}) introMessage !: HTMLDivElement;

    init() {
        // Do not continue if countdown is not rendered
        if (!this.display) {
            return;
        }

        // Prepare required data
        this.prepare();

        // Update visibility
        if (this.selectedVariant) {
            const productOptionPicker = document.getElementById('product-form-option-picker');
            if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
                const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;
                // eslint-disable-next-line max-len
                this.$subscribe(productOptionPickerComponent.onSelectedVariantChange, (variant: Shopify.Liquid.Variant|null) => {
                    if (variant) {
                        this.selectedVariant = variant;
                        this.updateVisibility(this.selectedVariant);
                    }
                });
            }
        }
        this.updateVisibility(this.selectedVariant);
    }

    prepare() {
        // Get shipping country
        let shippingCountry = 'GB';
        const shippingCountryElement = document.querySelector('.gep-switcher-flag');
        if (shippingCountryElement) {
            const shippingCountryElementStyle = shippingCountryElement.getAttribute('style');
            if (shippingCountryElementStyle !== null) {
                const shippingCountryElementStyleSplit = shippingCountryElementStyle.split('/');
                // eslint-disable-next-line max-len
                shippingCountry = shippingCountryElementStyleSplit[shippingCountryElementStyleSplit.length - 1].split('.png')[0].toUpperCase();
            }
        }

        // Determine current time
        const currentTime = this.calcTime();
        const currentHour = parseInt(currentTime[0], 10);
        const currentMinute = parseInt(currentTime[1], 10);
        const currentSecond = parseInt(currentTime[2], 10);

        // Determine daylight saving value
        let daylightSavingValue = 0;
        if (this.daylightSaving) {
            daylightSavingValue = 1;
        }

        // Set delivery cut-off time
        let maxHour = this.maxHour + daylightSavingValue;
        const maxMinute = 60;
        const maxSecond = 60;

        // Reduce delivery cut-off time if ordering from a non-UK country
        if (shippingCountry !== 'GB') {
            maxHour = maxHour - 1;
        }

        // Init countdown - convert time to UNIX
        const durationHours = (maxHour - currentHour - 1) * 60 * 60;
        const durationMinutes = (maxMinute - currentMinute) * 60;
        const durationseconds = (maxSecond - currentSecond);
        const duration = durationHours + durationMinutes + durationseconds;
        this.initCountdown(duration);
    }

    calcTime() {
        // Get time now with timezone offset
        const d = new Date();
        const utc = d.getTime() + (d.getTimezoneOffset() * 60000);
        const nd = new Date(utc);

        // Return time as a string
        const date = nd.toUTCString().split(' ');
        const clock = date[date.length - 2].split(':');
        return clock;
    }

    initCountdown(duration: number) {
        this.updateCountdown(duration);
        setInterval(() => {
            this.updateCountdown(duration);
            --duration;
        }, 1000);
    }

    updateCountdown(duration: number) {

        // Determine today & tomorrow
        const d = new Date();
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const today = d.getDay();
        let tomorrow = d.getDay() + 1;
        if (tomorrow === 5) {
            tomorrow = 0;
        }

        // If today is a weekend, or delivery cut off point is reached - show 'tomorrow' message
        if (duration <= 0 || today === 0 || today === 6) {
            let nextWorkingDay = '';
            if (today === 5 || today === 6) {
                nextWorkingDay = `on ${days[1]}`;
            } else {
                nextWorkingDay = 'tomorrow';
            }
            const tmrwMessage = `<span>${nextWorkingDay}</span>`;
            const newContent = `<span class="next-day">${this.nextDayMessage.replace('#nwd#', tmrwMessage)}</span>`;
            this.countdown.innerHTML = newContent;
            if (this.introMessage) {
                this.introMessage.remove();
            }

        // Eligable to show countdown message
        } else {

            // Get time values
            let hours = (duration / 3600) % 24;
            hours = Math.floor(hours);
            let hoursString = `<span>${hours}</span>`;
            if (hours < 10) {
                hoursString = `<span>0${hours}</span>`;
            }
            let minutes = (duration / 60) % 60;
            minutes = Math.floor(minutes);
            let minutesString = `<span>${minutes}</span>`;
            if (minutes < 10) {
                minutesString = `<span>0${minutes}</span>`;
            }
            let seconds = duration % 60;
            seconds = Math.floor(seconds);
            let secondsString = `<span>${seconds}</span>`;
            if (seconds < 10) {
                secondsString = `<span>0${seconds}</span>`;
            }

            // Define hour text
            let hourText = 'Hrs';
            if (hours === 1) {
                hourText = 'Hr';
            }

            // Define minute text
            let minuteText = 'Mins';
            if (minutes == 1) {
                minuteText = 'Min';
            }

            // Define second text
            let secondText = 'Secs';
            if (seconds == 1) {
                secondText = 'Sec';
            }

            // Define time
            const time = `
                <span class="time">
                    ${hoursString} ${hourText} ${minutesString} ${minuteText} ${secondsString} ${secondText}
                </span>
            `;

            // Update
            const newContent = this.countdownMessage.replace('#time#', time);
            this.countdown.innerHTML = newContent;
        }
    }

    updateVisibility(variant: Shopify.Liquid.Variant|null) {

        // Check if variant is null
        if (variant === null) {
            return;
        }

        // Check variant is in stock
        if (variant.available) {

            // Show
            this.classList.remove('delivery-countdown--hidden');
        } else {

            // Hide
            this.classList.add('delivery-countdown--hidden');
        }
    }
}
